import { useEffect, useState } from "react";
import {
  StandardTextStringModel,
  glossaryStandardTextStringApi,
} from "../api/glossaryStandardTextStringApi";
import { Problem } from "../api/backendApi";
import {
  ChevronExpand,
  Copy,
  PlusCircleFill,
  Search,
  TrashFill,
} from "react-bootstrap-icons";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Button, Form, InputGroup, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { authUtils } from "../common/authUtils";
import { useAuth } from "react-oidc-context";
import ContextMenuCopy from "../components/contextmenus/ContextMenuCopy";
import {
  Order,
  TableColumnSorter,
} from "../common/tableColumnSorting/tableColumnSorter";
import ArraySort from "../common/tableColumnSorting/arraySort";
import GlossaryTextModal from "../components/modals/GlossaryTextModal";

function StandardTextStringPage() {
  const auth = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [standardTextStrings, setStandardTextStrings] = useState<
    StandardTextStringModel[]
  >([]);

  const [columnSort, setColumnSort] = useState<
    TableColumnSorter<StandardTextStringModel> | undefined
  >(undefined);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [filter, setFilter] = useState<string>("");

  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [isCopy, setIsCopy] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<number | undefined>(undefined);
  const [modalText, setModalText] = useState<string>("");
  const [newStandardTextString, setNewStandardTextString] = useState<
    StandardTextStringModel | undefined
  >(undefined);
  const [standardTextStringBeingEdited, setStandardTextStringBeingEdited] =
    useState<StandardTextStringModel | undefined>(undefined);
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [contextMenuSelected, setContectMenuSelected] =
    useState<StandardTextStringModel>();
  const [contextMenuOptions, setContectMenuOptions] = useState<string[]>([]);

  useEffect(() => {
    async function loadStandardTextStrings() {
      setLoading(true);
      const data = await glossaryStandardTextStringApi.getStandardTextStrings();

      if (data.ok) {
        setError(undefined);
        setStandardTextStrings(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }

    loadStandardTextStrings();
  }, []);

  const applyColumnSort = (standardTexts: StandardTextStringModel[]) => {
    if (columnSort === undefined) {
      return standardTexts;
    }

    return [...standardTexts].sort(columnSort.compareFn);
  };

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    standardTextString?: StandardTextStringModel,
    isCopy: boolean = false,
  ) => {
    setModalText(modalText);
    setIsCreate(isCreate);
    setStandardTextStringBeingEdited(standardTextString);
    setNewStandardTextString(standardTextString);
    setShow(true);
    setIsCopy(isCopy);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLTableRowElement>) => {
    event.preventDefault();

    const position = {
      top: event.clientY,
      left: event.clientX,
    };

    setContextMenuPosition(position);
    setIsContextMenuVisible(true);
  };

  const deleteStandardTextString = (
    standardTextString: StandardTextStringModel,
  ) => {
    glossaryStandardTextStringApi
      .deleteStandardTextString(standardTextString.stringId)
      .then((response) => {
        if (response.ok) {
          setStandardTextStrings(
            standardTextStrings.filter((s) => s !== standardTextString),
          );
        } else {
          setError(response.problem);
        }
      });
  };

  const handleSubmit = (standardTextString: StandardTextStringModel) => {
    let call = isCreate
      ? glossaryStandardTextStringApi.createStandardTextString(
          standardTextString,
        )
      : glossaryStandardTextStringApi.updateStandardTextString(
          standardTextString.stringId,
          standardTextString,
        );

    return call;
  };

  const onNewStandardTextString = (
    standardTextString: StandardTextStringModel,
  ) => {
    if (
      standardTextStrings.find(
        (s) => s.stringId === standardTextString.stringId,
      )
    ) {
      let updatedStandardTextString = standardTextStrings.map(
        (s: StandardTextStringModel) => {
          return s.stringId === standardTextString?.stringId
            ? {
                searchOrder: standardTextString.searchOrder,
                stringId: standardTextString.stringId,
                english: standardTextString.english,
                dutch: standardTextString.dutch,
                german: standardTextString.german,
                french: standardTextString.french,
                spanish: standardTextString.spanish,
                portuguese: standardTextString.portuguese,
                italian: standardTextString.italian,
                polish: standardTextString.polish,
                czech: standardTextString.czech,
                swedish: standardTextString.swedish,
                danish: standardTextString.danish,
                norwegian: standardTextString.norwegian,
                finnish: standardTextString.finnish,
                hungarian: standardTextString.hungarian,
                croatian: standardTextString.croatian,
                hebrew: standardTextString.hebrew,
                slovene: standardTextString.slovene,
                russian: standardTextString.russian,
                arabic: standardTextString.arabic,
                ukrainian: standardTextString.ukrainian,
                isNew: standardTextString.isNew,
              }
            : s;
        },
      );

      setStandardTextStrings(updatedStandardTextString);
    } else {
      setStandardTextStrings([...standardTextStrings, standardTextString]);
    }

    setNewStandardTextString(undefined);
  };

  const onStandardTextStringChanged = (
    standardTextString: StandardTextStringModel,
  ) => {
    setError(undefined);
    let updatedStandardTextString = standardTextStrings.map(
      (s: StandardTextStringModel) => {
        return s === standardTextStringBeingEdited ? standardTextString : s;
      },
    );

    setStandardTextStrings(updatedStandardTextString);
    setStandardTextStringBeingEdited(undefined);
  };

  const handleColumnSort = (
    columnName: string,
    sortFunction: (
      sortOrder: Order,
    ) => (a: StandardTextStringModel, b: StandardTextStringModel) => number,
  ) => {
    const sortOrder: Order =
      columnSort !== undefined &&
      columnSort.columnName === columnName &&
      columnSort.sortBy === "asc"
        ? "desc"
        : "asc";

    setColumnSort({
      columnName,
      compareFn: sortFunction(sortOrder),
      sortBy: sortOrder,
    });
  };

    const copyToClipboard = async (text: string) => {
        await navigator.clipboard.writeText(text.replaceAll("<NL>", "\r\n"));
    }

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Glossary Title Texts
          {authUtils.isAdministrator(auth) && (
            <PlusCircleFill
              className="text-primary"
              onClick={() => handleShow(true, "Add Title Text", undefined)}
              width={32}
              height={32}
              style={{ marginLeft: "0.5rem" }}
              role="button"
            />
          )}
        </h1>
        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />
        {!loading && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  Filter
                </Form.Label>
              </span>
              <Form.Control
                size="sm"
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
                style={{ width: "400px" }}
              ></Form.Control>
            </InputGroup>
          </div>
        )}
        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "95%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover striped>
                <thead>
                  <tr>
                    <th style={{ width: "10%" }}>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                          maxWidth: "fit-content",
                        }}
                        onClick={() => {
                          handleColumnSort("StringId", (sortOrder) =>
                            ArraySort.byNumber(
                              (standardText) => standardText.stringId ?? 0,
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        String Id
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th style={{ width: "10%" }}>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          handleColumnSort("SearchOrder", (sortOrder) =>
                            ArraySort.byNumber(
                              (standardText) => standardText.searchOrder ?? 0,
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        Search Order
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th></th>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          handleColumnSort("English", (sortOrder) =>
                            ArraySort.byString(
                              (standardText) => standardText.english,
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        English
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th style={{ width: "10%" }}>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          handleColumnSort("IsNew", (sortOrder) =>
                            ArraySort.byNumber(
                              (attrText) => (attrText.isNew ? 1 : 0),
                              sortOrder,
                            ),
                          );
                        }}
                        className="table-header"
                      >
                        Is New
                        <ChevronExpand
                          className="text-primary"
                          style={{
                            display: "flex",
                            margin: "1px",
                            borderLeft: "1px solid #0d6efd",
                            marginLeft: "5px",
                          }}
                        />
                      </Form.Label>
                    </th>
                    <th style={{ width: "1px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {applyColumnSort(
                    standardTextStrings.filter(
                      (standardTextString) =>
                        standardTextString.english
                          ?.toUpperCase()
                          .includes(filter.toUpperCase()),
                    ),
                  ).map((standardTextString) => (
                    <tr
                      className="table-row-scale"
                      role="button"
                      key={standardTextString.stringId}
                      onClick={() => {
                        if (authUtils.isAdministrator(auth)) {
                          handleShow(
                            false,
                            "Edit Title Text",
                            standardTextString,
                          );
                        }
                      }}
                      onContextMenu={(e) => {
                        handleContextMenu(e);
                        setContectMenuSelected(standardTextString);
                        let options: string[] = ["Create Copy"];

                        setContectMenuOptions(options);
                      }}
                    >
                      <td style={{ fontWeight: "600" }}>
                        {standardTextString.stringId?.toString()}
                      </td>
                      <td>{standardTextString.searchOrder?.toString()}</td>
                      <td>
                        <OverlayTrigger
                          placement="right"
                          show={showToast === standardTextString.stringId}
                          delay={{ show: 0, hide: 0 }}
                          overlay={
                            <Tooltip id="tooltip-right">
                              <strong>Copied!</strong>
                            </Tooltip>
                          }
                          >
                          <Button variant="outline-primary" size="sm" onClick={(e) => {
                            e.stopPropagation();
                            setShowToast(standardTextString.stringId!);
                            copyToClipboard(standardTextString.english!);
                            setTimeout(() => setShowToast(undefined), 1500);
                            }}>
                              <Copy />
                          </Button>
                        </OverlayTrigger>
                      </td>
                      <td className="table-data-width" style={{ whiteSpace: "pre" }}>
                        {standardTextString.english}
                      </td>
                      <td>{standardTextString.isNew ? "Yes" : "No"}</td>
                      <td>
                        {authUtils.isAdministrator(auth) && (
                          <TrashFill
                            className="text-danger"
                            height={24}
                            width={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                window.confirm(
                                  "Are you sure this text can be deleted?",
                                )
                              ) {
                                deleteStandardTextString(standardTextString);
                              }
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
      {show && authUtils.isAdministrator(auth) ? (
        <GlossaryTextModal
          glossaryText={
            newStandardTextString ?? {
              stringId: undefined,
              searchOrder: undefined,
              english: undefined,
              dutch: undefined,
              german: undefined,
              french: undefined,
              spanish: undefined,
              portuguese: undefined,
              italian: undefined,
              polish: undefined,
              czech: undefined,
              swedish: undefined,
              danish: undefined,
              norwegian: undefined,
              finnish: undefined,
              hungarian: undefined,
              croatian: undefined,
              hebrew: undefined,
              slovene: undefined,
              russian: undefined,
              arabic: undefined,
              ukrainian: undefined,
            }
          }
          searchOrders={standardTextStrings.map((s) => s.searchOrder)}
          isCreate={isCreate}
          isCopy={isCopy}
          onSubmit={handleSubmit}
          onChange={
            isCreate ? onNewStandardTextString : onStandardTextStringChanged
          }
          onCancel={() => {
            setNewStandardTextString(undefined);
            setStandardTextStringBeingEdited(undefined);
            setShow(false);
          }}
          modalText={modalText}
          type="standard"
        />
      ) : null}
      {isContextMenuVisible && authUtils.isAdministrator(auth) && (
        <ContextMenuCopy
          onClose={() => setIsContextMenuVisible(false)}
          onCreateCopy={() =>
            handleShow(
              true,
              "Add Title text",
              {
                stringId: undefined,
                searchOrder: undefined,
                english: contextMenuSelected?.english,
                dutch: contextMenuSelected?.dutch,
                german: contextMenuSelected?.german,
                french: contextMenuSelected?.french,
                spanish: contextMenuSelected?.spanish,
                portuguese: contextMenuSelected?.portuguese,
                italian: contextMenuSelected?.italian,
                polish: contextMenuSelected?.polish,
                czech: contextMenuSelected?.czech,
                swedish: contextMenuSelected?.swedish,
                danish: contextMenuSelected?.danish,
                norwegian: contextMenuSelected?.norwegian,
                finnish: contextMenuSelected?.finnish,
                hungarian: contextMenuSelected?.hungarian,
                croatian: contextMenuSelected?.croatian,
                hebrew: contextMenuSelected?.hebrew,
                slovene: contextMenuSelected?.slovene,
                russian: contextMenuSelected?.russian,
                arabic: contextMenuSelected?.arabic,
                ukrainian: contextMenuSelected?.ukrainian,
              },
              true,
            )
          }
          options={contextMenuOptions}
          style={{
            position: "absolute",
            top: contextMenuPosition.top,
            left: contextMenuPosition.left,
          }}
        />
      )}
    </>
  );
}

export default StandardTextStringPage;
